import { Layout } from '@community_dev/pixels'
import { ApiError, ErrorObject } from '@community_dev/requests'
import { Alert, Button, Form, Input, Space, Tooltip } from 'antd'
import camelCase from 'lodash/camelCase'
import omit from 'lodash/omit'
import { useMemo, type JSX } from 'react'

import type { CreateSSOConfigInput } from 'api/sso'
import { ButtonModalPrompt } from 'components/ButtonModalPrompt/ButtonModalPrompt'

type SSOFormValues = Omit<CreateSSOConfigInput, 'ssoDomains'> & { domains: string }

export type SSOFormSubmitValues = CreateSSOConfigInput

type Props = {
  initialValues?: SSOFormSubmitValues
  isSaving?: boolean
  isDeleting?: boolean
  showDelete?: boolean
  canEdit?: boolean
  onSubmit: (values: SSOFormSubmitValues) => void
  onDelete?: () => void
  error?: ApiError | null
}

type SSOFormError = {
  field?: string
  message: string
}

const FORM_FIELDS: ReadonlyArray<keyof SSOFormValues> = [
  'entityId',
  'signinUrl',
  'metadataUrl',
  'certificate',
  'domains',
]

const toField = (formError: ErrorObject): string | undefined => {
  const field = (formError as { field?: unknown }).field
  return typeof field === 'string' && field.length > 0 ? field : undefined
}

// The backend returns ssoDomains, but we want to use domains since we have that field in the form
const toNormalizedField = (field?: string): string | undefined => {
  if (!field) return undefined
  const normalized = camelCase(field)
  return normalized === 'ssoDomains' ? 'domains' : normalized
}

type ErrorGroups = {
  fieldErrors: Partial<Record<keyof SSOFormValues, string>>
  generalErrors: string[]
}

const toErrorGroups = (rawErrors: ErrorObject[] | undefined): ErrorGroups => {
  // transform the errors into a more usable shape{ field: string, message: string }[]
  const errors = (rawErrors ?? [])
    .map<SSOFormError | null>((rawError) => {
      const message = rawError.message ?? rawError.detail
      if (!message) {
        return null
      }

      const field = toNormalizedField(toField(rawError))

      return {
        field,
        message,
      }
    })
    .filter((item): item is SSOFormError => item !== null)

  // group the errors by field
  return errors.reduce(
    (acc, { field, message }) => {
      // Form errors are grouped by field
      if (field && FORM_FIELDS.includes(field as keyof SSOFormValues)) {
        const fieldKey = field
        const existing = acc.fieldErrors[fieldKey]

        return {
          fieldErrors: {
            ...acc.fieldErrors,
            [fieldKey]: existing ? `${existing}, ${message}` : message,
          },
          generalErrors: acc.generalErrors,
        }
      }

      // General errors are not grouped by field
      const formattedMessage = field ? `${field}: ${message}` : message
      return {
        fieldErrors: acc.fieldErrors,
        generalErrors: [...acc.generalErrors, formattedMessage],
      }
    },
    { fieldErrors: {}, generalErrors: [] } as ErrorGroups,
  )
}

export function SSOConfigForm({
  initialValues,
  isSaving,
  isDeleting,
  showDelete,
  canEdit = true,
  onSubmit,
  onDelete,
  error,
}: Props): JSX.Element {
  const [form] = Form.useForm<SSOFormValues>()

  const defaults = useMemo(
    () =>
      initialValues
        ? {
            ...omit(initialValues, 'ssoDomains'),
            domains: initialValues?.ssoDomains.map(({ domain }) => domain.trim()).join(', '),
          }
        : undefined,
    [initialValues],
  )

  const { fieldErrors, generalErrors } = useMemo(() => toErrorGroups(error?.errors), [error])

  const readOnlyTooltip = !canEdit ? 'You do not have permission to modify SSO configs.' : undefined

  const handleFinish = (formValues: SSOFormValues) => {
    if (!canEdit) return
    const payload: SSOFormSubmitValues = {
      entityId: formValues.entityId,
      signinUrl: formValues.signinUrl,
      certificate: formValues.certificate,
      ssoDomains: String(formValues.domains)
        .split(',')
        .map((domain) => domain.trim())
        .filter(Boolean)
        .map((domain) => ({ domain })),
      metadataUrl: formValues.metadataUrl?.trim() || undefined,
    }

    onSubmit(payload)
  }

  return (
    <Form form={form} initialValues={defaults} layout="vertical" onFinish={handleFinish} style={{ maxWidth: 760 }}>
      {generalErrors.length > 0 && (
        <Layout marginBottom="8px">
          {generalErrors.map((message, idx) => (
            <Alert key={idx} message={message} showIcon style={{ marginBottom: 8 }} type="error" />
          ))}
        </Layout>
      )}
      <Form.Item
        extra="A unique identifier for your identity Provider (IdP). Often a URL or URI provided by your IdP."
        help={fieldErrors.entityId}
        label="Entity ID"
        name="entityId"
        rules={[{ required: true, message: 'Entity ID is required' }]}
        validateStatus={fieldErrors.entityId ? 'error' : undefined}
      >
        <Input placeholder="https://www.company.com/saml" />
      </Form.Item>

      <Form.Item
        extra="The SSO endpoint where authentication requests are sent. Also known as the SAML SSO URL."
        help={fieldErrors.signinUrl}
        label="Sign-In URL"
        name="signinUrl"
        rules={[{ required: true, message: 'Sign-In URL is required' }]}
        validateStatus={fieldErrors.signinUrl ? 'error' : undefined}
      >
        <Input placeholder="https://www.company.com/saml" />
      </Form.Item>
      <Form.Item
        extra="Optional URL that lets Community automatically retrieve and configure all the necessary IdP info at once."
        help={fieldErrors.metadataUrl}
        label="Metadata URL"
        name="metadataUrl"
        validateStatus={fieldErrors.metadataUrl ? 'error' : undefined}
      >
        <Input placeholder="https://www.company.com/metadata" />
      </Form.Item>

      <Form.Item
        extra="The X.509 Certificate used to verify SAML Assertions. Paste the full certificate."
        help={fieldErrors.certificate}
        label="Public Key"
        name="certificate"
        rules={[{ required: true, message: 'Public Key is required' }]}
        validateStatus={fieldErrors.certificate ? 'error' : undefined}
      >
        <Input.TextArea placeholder="-----BEGIN CERTIFICATE-----\nSample data\n-----END CERTIFICATE-----" rows={6} />
      </Form.Item>

      <Form.Item
        extra="One or more domains used to match users to the SSO config (e.g., yourcompany.com)"
        help={fieldErrors.domains}
        label="Domains"
        name="domains"
        rules={[{ required: true, message: 'Domains are required' }]}
        validateStatus={fieldErrors.domains ? 'error' : undefined}
      >
        <Input placeholder="company.com, company.uk, sub-domain.company.com" />
      </Form.Item>

      <Layout display="flex" justifyContent="space-between" marginTop="8px">
        <Layout>
          {showDelete && onDelete && (
            <Tooltip title={readOnlyTooltip}>
              <span>
                <ButtonModalPrompt
                  buttonText="Delete"
                  buttonType="primary"
                  danger
                  disabled={isDeleting || !canEdit}
                  modalText="This will remove SSO settings for this client. This action cannot be undone."
                  modalTitle={`Delete SSO Configuration for ${defaults?.domains}?`}
                  onClick={() => {
                    if (!canEdit) return
                    onDelete()
                  }}
                />
              </span>
            </Tooltip>
          )}
        </Layout>

        <Layout>
          <Space>
            <Form.Item noStyle shouldUpdate>
              {() => {
                const hasErrors = form.getFieldsError().some(({ errors }) => errors.length)
                const values = form.getFieldsValue(['entityId', 'signinUrl', 'certificate', 'domains'])
                const isEmpty = Object.values(values).some((value) => !value)
                const disabled = hasErrors || isEmpty || !canEdit
                const button = (
                  <Button disabled={disabled} htmlType="submit" loading={isSaving && canEdit} type="primary">
                    Save
                  </Button>
                )

                if (readOnlyTooltip) {
                  return (
                    <Tooltip title={readOnlyTooltip}>
                      <span>{button}</span>
                    </Tooltip>
                  )
                }

                return button
              }}
            </Form.Item>
          </Space>
        </Layout>
      </Layout>
    </Form>
  )
}
